<script setup lang="ts">
import mdiData from '@iconify-json/mdi/icons.json'

import type { MiniGame, WheelSegment } from '~/stores/challengeWheel'

const SPIN_DURATION_MS = 5000
const CX = 250
const CY = 250
const RADIUS = 225
// Radial position of the icon's centre along each spoke
const ICON_CENTER_RADIUS = RADIUS * 0.88
// Half-width of the 24×24 icon (SVG units)
const ICON_HALF = 12
// Gap between icon edge and text anchor
const TEXT_GAP = 6

const GAME_ICON_MAP: Record<MiniGame, string> = {
  'bible-trivia': 'help-circle-outline',
  'categories': 'view-grid-outline',
  'sword-drill': 'book-cross',
  'word-scramble': 'alphabetical-variant',
}

interface SegmentData extends WheelSegment {
  centerAngle: number
  endAngle: number
  // Position of the group pivot (icon centre) in SVG space
  groupX: number
  groupY: number
  iconBody: string
  path: string
  startAngle: number
  // Rotation applied to the label group so it lies along the spoke (always outward)
  textRotation: number
}

function getMdiIconBody(name: string): string {
  const icon = (mdiData.icons as Record<string, { body: string }>)[name]
  return icon?.body ?? ''
}

function polarToCart(r: number, angleDeg: number): { x: number, y: number } {
  const rad = (angleDeg - 90) * (Math.PI / 180)
  return { x: CX + r * Math.cos(rad), y: CY + r * Math.sin(rad) }
}

function segmentPath(startAngle: number, endAngle: number): string {
  const s = polarToCart(RADIUS, startAngle)
  const e = polarToCart(RADIUS, endAngle)
  const large = endAngle - startAngle > 180 ? 1 : 0
  return `M ${CX} ${CY} L ${s.x.toFixed(3)} ${s.y.toFixed(3)} A ${RADIUS} ${RADIUS} 0 ${large} 1 ${e.x.toFixed(3)} ${e.y.toFixed(3)} Z`
}

const store = useChallengeWheelStore()
const { data: ui } = useChallengeWheelUI()

const isAnimating = ref(false)
let spinTimeout: null | ReturnType<typeof setTimeout> = null

const segments = computed<SegmentData[]>(() => {
  const segs = store.wheelSegments
  if (segs.length === 0) {
    return []
  }
  const sliceAngle = 360 / segs.length
  return segs.map((seg, i) => {
    const startAngle = i * sliceAngle
    const endAngle = (i + 1) * sliceAngle
    const centerAngle = startAngle + sliceAngle / 2
    const gp = polarToCart(ICON_CENTER_RADIUS, centerAngle)
    // Always rotate so local +x points outward along the spoke.
    // Every segment reads consistently from center → outward.
    const textRotation = centerAngle - 90
    return {
      ...seg,
      centerAngle,
      endAngle,
      groupX: gp.x,
      groupY: gp.y,
      iconBody: getMdiIconBody(GAME_ICON_MAP[seg.type]),
      path: segmentPath(startAngle, endAngle),
      startAngle,
      textRotation,
    }
  })
})

const wheelStyle = computed(() => ({
  transform: `rotate(${store.wheelRotation}deg)`,
  transformBox: 'fill-box' as const,
  transformOrigin: 'center' as const,
  transition: isAnimating.value
    ? `transform ${SPIN_DURATION_MS}ms cubic-bezier(0.25, 0.05, 0.08, 1)`
    : 'none',
}))

const isSpinnable = computed(() => store.phase === 'idle')

function handleTap() {
  if (!isSpinnable.value) {
    return
  }
  store.spinWheel()
}

watch(() => store.phase, phase => {
  if (phase === 'spinning') {
    isAnimating.value = true
    spinTimeout = setTimeout(() => {
      isAnimating.value = false
      store.resolveSpin()
    }, SPIN_DURATION_MS)
  }
})

onUnmounted(() => {
  if (spinTimeout !== null) {
    clearTimeout(spinTimeout)
  }
})
</script>

<template>
  <!-- Outer div fills the flex-1 parent panel so % sizing works -->
  <div class="flex h-full w-full flex-col items-center justify-center gap-3">
    <!-- Square container sized to fit within the viewport -->
    <div
      class="relative"
      style="width: min(100%, calc(100vh - 6rem)); aspect-ratio: 1"
    >
      <!-- Wheel SVG — fills the square container -->
      <svg
        v-click="handleTap"
        :class="[
          'select-none touch-none w-full h-full',
          isSpinnable ? 'cursor-pointer' : 'cursor-default',
        ]"
        viewBox="0 0 500 500"
      >
        <defs>
          <filter
            id="pointer-shadow"
            x="-20%"
            y="-20%"
            width="140%"
            height="140%"
          >
            <feDropShadow
              dx="0"
              dy="2"
              stdDeviation="4"
              flood-color="rgba(0,0,0,0.6)"
            />
          </filter>
        </defs>

        <!-- Rotating segments group -->
        <g :style="wheelStyle">
          <path
            v-for="(seg, i) in segments"
            :key="`seg-${i}`"
            :d="seg.path"
            :fill="seg.color"
            stroke="white"
            stroke-width="1.5"
          />
          <!-- Alternating inner shading for contrast between same-color neighbours -->
          <path
            v-for="(seg, i) in segments"
            :key="`shade-${i}`"
            :d="seg.path"
            :fill="i % 2 === 0 ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'"
            class="pointer-events-none"
          />
          <!-- Segment label groups: icon (outer) + text (inner), both on the spoke -->
          <!-- translate(groupX,groupY) moves origin to icon centre; rotate aligns x-axis along spoke -->
          <g
            v-for="(seg, i) in segments"
            :key="`label-${i}`"
            :transform="`translate(${seg.groupX.toFixed(2)},${seg.groupY.toFixed(2)}) rotate(${seg.textRotation.toFixed(2)})`"
            class="pointer-events-none select-none"
            fill="white"
          >
            <!-- Icon centred at local (0,0) — outer side of the spoke -->
            <g transform="translate(-12,-12)">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                opacity="0.92"
                v-html="seg.iconBody"
              />
            </g>
            <!-- Text left-justified inward from the icon (local -x = toward centre) -->
            <text
              :x="-(ICON_HALF + TEXT_GAP)"
              y="0"
              text-anchor="end"
              dominant-baseline="middle"
              font-family="system-ui, sans-serif"
              font-size="18"
              font-weight="800"
            >{{ seg.label }}</text>
          </g>
        </g>

        <!-- Center hub -->
        <circle
          :cx="CX"
          :cy="CY"
          r="34"
          fill="#0f172a"
          stroke="white"
          stroke-width="1.5"
        />
        <text
          :x="CX"
          :y="CY"
          class="pointer-events-none select-none"
          dominant-baseline="middle"
          fill="white"
          font-family="system-ui, sans-serif"
          font-size="14"
          font-weight="800"
          text-anchor="middle"
        >{{ ui?.wheel.hubLabel }}</text>

        <!-- Pointer arrow — overlaid on wheel, tip pointing toward center from top -->
        <polygon
          points="250,34 232,10 268,10"
          fill="#0f172a"
          stroke="white"
          stroke-linejoin="round"
          stroke-width="1.5"
          filter="url(#pointer-shadow)"
          class="pointer-events-none"
        />
      </svg>
    </div>
  </div>
</template>
